import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  errorKind: "chunk" | "runtime" | null;
}

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message ?? "";
  if (error.name === "ChunkLoadError") return true;
  return (
    /Loading chunk \d+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg)
  );
}

export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, errorKind: null };

  static getDerivedStateFromError(error: unknown): State {
    return {
      hasError: true,
      errorKind: isChunkLoadError(error) ? "chunk" : "runtime",
    };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (isChunkLoadError(error)) {
      // Stale chunk after a new deploy — the cached index.html still
      // references hashes that no longer exist on the CDN. Force a hard
      // reload to pick up the new index.html and rehydrate.
      //
      // Anti-loop guard: only auto-reload once per browser session. If we
      // reload and the same chunk error fires again, fall through to the
      // visible fallback so the user can read the message and decide.
      if (typeof window !== "undefined") {
        try {
          const key = "rc:chunkReloadAttempted";
          if (!sessionStorage.getItem(key)) {
            sessionStorage.setItem(key, "1");
            window.location.reload();
            return;
          }
        } catch {
          window.location.reload();
          return;
        }
      }
    }
    if (typeof console !== "undefined") {
      console.error("RouteErrorBoundary caught:", error, info);
    }
  }

  handleReload = () => {
    if (typeof window !== "undefined") window.location.reload();
  };

  handleHome = () => {
    if (typeof window !== "undefined") window.location.assign("/");
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="grid min-h-screen place-items-center bg-white px-6 text-center">
        <div className="max-w-md">
          <p className="font-mono text-[11px] uppercase tracking-caps text-rc-gray-mid">
            Reach Central AI
          </p>
          <h1 className="mt-6 text-2xl font-bold text-rc-black">
            Quelque chose s&apos;est mal passé.
          </h1>
          <p className="mt-2 text-sm text-rc-gray-mid">
            Something went wrong loading this page.
          </p>
          <p className="mt-6 text-rc-gray-text">
            Recharge la page pour réessayer. Si le problème persiste,
            écris-nous à hello@reachcentral.io.
          </p>
          <p className="mt-2 text-sm text-rc-gray-mid">
            Reload the page to try again. If the issue persists, write to
            hello@reachcentral.io.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={this.handleReload}
              className="inline-flex min-h-[44px] items-center justify-center rounded-pill bg-rc-teal-bright px-6 py-3 text-sm font-semibold text-white"
            >
              Recharger / Reload
            </button>
            <button
              type="button"
              onClick={this.handleHome}
              className="inline-flex min-h-[44px] items-center justify-center rounded-pill border border-rc-rule bg-white px-6 py-3 text-sm font-semibold text-rc-black"
            >
              Accueil / Home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
